import { environmentFor, type TenantEntry } from '@linksoft/tenant-registry';
import type { CustomerConfigState } from '../types/customer';
import { resolvePortalSettings, toFeatureStates, toPageStates } from './resolve-settings';

/**
 * Flatten a resolved registry entry into the app's `CustomerConfigState`.
 *
 * Pure and dependency-free on purpose: the build modules, `frogger.options.ts`
 * and the Vue app all call it, so it must import neither Nuxt nor Node.
 */
export function toCustomerConfigState(entry: TenantEntry): CustomerConfigState {
	const { customer, deployment } = entry;
	const name = deployment.displayName ?? customer.name;
	const environment = environmentFor(entry);
	const settings = resolvePortalSettings(entry);

	return {
		id: customer.id,
		name,
		productType: deployment.productType,
		portalType: deployment.portalType!,
		releaseLevel: deployment.releaseLevel,
		key: deployment.key,
		apiOrigin: environment.endpoints.api,
		...(settings.allowApiOriginOverride === undefined
			? {}
			: { allowApiOriginOverride: settings.allowApiOriginOverride }),
		...(environment.observability ? { observability: environment.observability } : {}),
		features: toFeatureStates(settings.features),
		pages: toPageStates(settings.pages),
		...(settings.homePage ? { homePage: settings.homePage } : {}),
		...(settings.comingSoon ? { comingSoon: settings.comingSoon } : {}),
		branding: {
			id: customer.id,
			name,
			assets: customer.brand.assets,
			// Deployment values win: one customer's portal deployments differ in
			// manifest description (nationwide customer vs supplier, for instance).
			...(customer.brand.manifest || deployment.manifest
				? { manifest: { ...customer.brand.manifest, ...deployment.manifest } }
				: {}),
		},
	};
}
